import '@awesome.me/webawesome/dist/components/badge/badge.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '../components/breadcrumb.js';
import '../components/empty_state.js';
import '../components/icon.js';

import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import { type IInvitation } from '../libraries/account_models.js';
import {
    answerInvitation,
} from '../libraries/api_client/invitation.js';
import { getUserInfo } from '../libraries/auth.js';
import { ConfirmController } from '../libraries/confirm_controller.js';
import { type InvitationAnswer, invitationAnswerConfirm } from '../libraries/confirm_presets.js';
import { formatGermanDate } from '../libraries/date_format.js';
import { notifyAccessChanged } from '../libraries/events.js';
import { formDialogHostStyles } from '../libraries/form_dialog_controller.js';
import { formConfirmed, formOutcomeOf, type FormSubmitResult } from '../libraries/form_outcome.js';
import { invitationStatusBadge } from '../libraries/invitation_columns.js';
import {
    getInvitations,
    invalidateInvitationsCache,
} from '../libraries/invitations_store.js';
import {
    type IListResult,
    ListController,
    listHostStyles,
} from '../libraries/list_controller.js';
import {
    actionsColumn,
    followSpaLink,
    type IListColumn,
} from '../libraries/list_table.js';
import { onSessionUpdate } from '../libraries/local_storage.js';
import { resetPermissionsCache } from '../libraries/permissions.js';
import { reportRowSuccess } from '../libraries/row_outcome.js';
import { formatDisplayName } from '../libraries/user.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';

const statusRank: Record<string, number> = {
    pending: 0,
    accepted: 1,
    declined: 2,
};
const unknownStatusRank = 3;

/**
 * Compares two invitations so that `pending` rows sort before
 * `accepted` rows, which in turn sort before `declined` rows.
 * Unknown statuses fall through to the bottom. The sort is stable
 * (ES2019+), so within each status group rows keep the
 * `createdAt desc` order from the backend's
 * `listReceivedInvitations` query.
 * @param a - First invitation.
 * @param b - Second invitation.
 * @returns A negative number when `a` should sort before `b`.
 */
function compareInvitationsByStatus (a: IInvitation, b: IInvitation): number {
    const rankA = statusRank[a.status] ?? unknownStatusRank;
    const rankB = statusRank[b.status] ?? unknownStatusRank;

    return rankA - rankB;
}

const roleLabels: Record<string, string> = {
    'role:administration:manage': 'Verwaltung',
    'role:organization:read': 'Lesen',
    'role:organization:write': 'Schreiben',
    'role:organization:manage': 'Verwalten',
    'role:farm:read': 'Lesen',
    'role:farm:document': 'Dokumentieren',
    'role:farm:write': 'Schreiben',
    'role:farm:publish': 'Veröffentlichen',
};

const typeLabels: Record<string, string> = {
    admin: 'Verwaltung',
    organization: 'Organisation',
    farm: 'Betrieb',
};

const answerConfirmations: Record<InvitationAnswer, string> = {
    accept: 'Einladung erfolgreich angenommen.',
    decline: 'Einladung abgelehnt.',
};

/** One staged answer: the invitation, and which way it is being answered. */
interface IStagedInvitationAnswer {
    invitation: IInvitation;
    answer: InvitationAnswer;
}

/**
 * Top-level "Einladungen" page that lists invitations sent to the
 * current user's e-mail address. Replaces the former tab inside
 * `<fm-account>` and is reachable from the user-dropdown entry and
 * the dashboard banner. Shares its data source with the dropdown
 * badge and the dashboard banner via `libraries/invitations_store.ts`.
 */
@customElement('fm-invitations')
export class FmInvitations extends LitElement {

    static styles = [
        listHostStyles,
        formDialogHostStyles,
        css`
        tbody td {
            white-space: nowrap;
        }
    `,
    ];

    private readonly list = new ListController<IInvitation>(this, {
        label: 'Einladungen',
        columns: (): Array<IListColumn<IInvitation>> => this.columns,
        rowKey: (invitation): string => invitation.id,
        errorDescription: 'Die Einladungen konnten nicht geladen werden.',
        load: async (): Promise<IListResult<IInvitation>> => ({
            rows: (await getInvitations()).slice().sort(compareInvitationsByStatus),
        }),
        empty: (): TemplateResult => this.renderEmptyState(),
    });

    /**
     * The question over both answers, which SC 3.3.4 asks for: accepting consumes the
     * invitation and grants the access, declining ends it for good, and neither is
     * undoable by the person who clicked. What the dialog guards is the wrong row.
     *
     * The sink declares no `reload`: answering busts the shared invitations cache, and
     * the `fm-invitations-changed` that follows is what pulls the new rows into this
     * page.
     */
    private readonly answer = new ConfirmController<IStagedInvitationAnswer>(
        this,
        staged => invitationAnswerConfirm({
            subjectName: this.invitationName(staged.invitation),
            roleLabel: roleLabels[staged.invitation.role] ?? staged.invitation.role,
            answer: staged.answer,
        }, {
            run: async (): Promise<FormSubmitResult> => this.submitAnswer(staged),
            onSuccess: async (message): Promise<void> => reportRowSuccess(this.list.rowSink({
                reload: false,
            }), message),
        })
    );

    /**
     * Reloads against the identity that is now signed in. The rows are dropped
     * first, because they belong to the account that just went away; the shared
     * store resets its own cache from its own `storage` listener, so the reload
     * re-fetches rather than replaying the previous user's payload.
     */
    private readonly windowEvents = new WindowEventsController(this, [
        onSessionUpdate((): void => {
            this.list.reset();
            void this.list.refresh();
        }),

        // Quiet: this reload follows a mutation whose own callout is already on
        // screen, and the rows it would replace are still good.
        onWindowEvent('fm-invitations-changed', () => {
            void this.list.refresh({
                surfaceFailure: false,
            });
        }),
    ]);

    /**
     * Answers one invitation.
     *
     * A refusal goes back to the dialog rather than above the rows, so the reason
     * arrives where the user is looking and the dialog stays open for another attempt.
     * @param staged - The invitation and the answer the dialog is confirming.
     * @returns What the request reported.
     */
    private async submitAnswer (staged: IStagedInvitationAnswer): Promise<FormSubmitResult> {
        const { invitation, answer } = staged;

        return formOutcomeOf(
            await answerInvitation(invitation.id, answer),
            (): FormSubmitResult => {
                if (answer === 'accept') {

                    // Accepting can flip the user into admin, add an organisation, and add
                    // farms in one step. Reset the snapshot before announcing the change so
                    // anything the refetch triggers reads a cleared cache.
                    resetPermissionsCache();
                    notifyAccessChanged();
                }

                // Bust the shared invitations cache so the dashboard banner and dropdown
                // badge refresh; the `fm-invitations-changed` listener above is what pulls
                // the new list into this page, which is why this surface declares no
                // `reload` of its own.
                invalidateInvitationsCache();

                return formConfirmed(answerConfirmations[answer]);
            }
        );
    }

    private getDeeplinkTarget (inv: IInvitation): {
        href: string;
        label: string;
    } | null {
        if (inv.type === 'farm' && inv.farmId !== null) {
            const href = inv.organizationId !== null ?
                `/organizations/${inv.organizationId}/farms/${inv.farmId}` :
                `/farms/${inv.farmId}`;

            return {
                href,
                label: 'Zum Betrieb',
            };
        }

        if (inv.type === 'organization' && inv.organizationId !== null) {
            return {
                href: `/organizations/${inv.organizationId}`,
                label: 'Zur Organisation',
            };
        }

        if (inv.type === 'admin') {
            return {
                href: '/administration/access',
                label: 'Zur Verwaltung',
            };
        }

        return null;
    }

    private renderInvitationActions (inv: IInvitation): TemplateResult | typeof nothing {
        if (inv.status === 'pending') {
            return html`
                <span class="fm-action-pair">
                    <wa-button variant="brand" size="s"
                               @click=${(): void => this.answer.stage({
                                    invitation: inv,
                                    answer: 'accept',
                                })}>
                        Annehmen
                    </wa-button>
                    <wa-button variant="danger" appearance="outlined" size="s"
                               @click=${(): void => this.answer.stage({
                                    invitation: inv,
                                    answer: 'decline',
                                })}>
                        Ablehnen
                    </wa-button>
                </span>
            `;
        }

        if (inv.status === 'accepted') {
            const target = this.getDeeplinkTarget(inv);

            if (target === null) {
                return nothing;
            }

            return html`
                <wa-button href=${target.href}
                           variant="brand" appearance="plain" size="s"
                           data-action="deeplink"
                           @click=${(event: MouseEvent): void => {
                                followSpaLink(event, target.href);
                            }}>
                    ${target.label}
                </wa-button>
            `;
        }

        return nothing;
    }

    private invitationName (inv: IInvitation): string {
        if (inv.type === 'admin') {
            return 'Düngeplanung';
        }

        if (inv.type === 'farm') {
            return inv.farmNameWithLegalForm ?? '—';
        }

        return inv.organizationNameWithLegalForm ?? '—';
    }

    private get columns (): Array<IListColumn<IInvitation>> {
        return [
            {
                label: 'Typ',
                track: 'xs',
                cell: inv => typeLabels[inv.type] ?? inv.type,
            },
            {
                label: 'Name',
                track: 'xl',
                cell: inv => this.invitationName(inv),
            },
            {
                label: 'Berechtigung',
                cell: inv => roleLabels[inv.role] ?? inv.role,
            },
            {
                label: 'Eingeladen am',
                cell: inv => formatGermanDate(inv.createdAt),
            },
            {
                label: 'Status',
                cell: inv => invitationStatusBadge(inv.status),
            },
            actionsColumn<IInvitation>(inv => this.renderInvitationActions(inv), 2),
        ];
    }

    private renderEmptyState (): TemplateResult {
        return html`
            <fm-empty-state .icon=${faEnvelope}
                            heading="Keine ausstehenden Einladungen"
                            description="Sobald Sie eine Einladung erhalten, erscheint sie hier.">
            </fm-empty-state>
        `;
    }

    private readonly userDisplayName = formatDisplayName(getUserInfo());

    render (): TemplateResult {
        return html`
            ${this.list.render(html`
                <fm-breadcrumb slot="breadcrumb" .items=${[
                    {
                        label: this.userDisplayName,
                    },
                ]}></fm-breadcrumb>
            `)}
            ${this.answer.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-invitations': FmInvitations;
    }
}
